import React, { useEffect, useState } from 'react';

import Alert from '@amzn/meridian/alert';
import Text from '@amzn/meridian/text';

import { ColumnState, ValueFormatterParams } from 'ag-grid-community';
import { clone, cloneDeep, isEqual, uniqWith } from 'lodash';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { Status } from 'src/constants';
import { EXCEL_DATE_STYLE_ID } from 'src/constants/agGrid';
import { BREADCRUMB_HEIGHT, FOOTER_HEIGHT, HEIGHT_ADJUSTMENT, TOPBAR_HEIGHT } from 'src/constants/style';
import { AddDealContext } from 'src/contexts/addDealContext';
import {
    IClonedSpecifyDealAgGridRowData,
    IConfirmDealAgGridRowData,
    ISpecifyDealAgGridRowData
} from 'src/types/IAddDeal';
import { IDealsRowData } from 'src/types/IDeals';
import { getWindowSize } from 'src/util/PlanMappings';
import {
    addDays,
    convertTimestampToYYYYMMDDInUTCTimezone,
    deductDays,
    getDateAndTimestamp,
    getYearMonthDayFormatFromISOString
} from 'src/util/dateTimeUtil';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { getDealStatusCellStyle } from 'src/util/grid/cellStyle';
import { dateFilterOptions } from 'src/util/grid/filter';
import { dateComparator, dateFilterComparator } from 'src/util/grid/sort';

import './ConfirmDeal.scss';

export const ConfirmDeal = () => {
    const { deals, specifyDealGridRowData, statusToDealsObj, setStatusToDealsObj, requireRerun, setRequireRerun } =
        React.useContext(AddDealContext);
    const [confirmDealGridRowData, setConfirmDealGridRowData] = useState<IConfirmDealAgGridRowData[]>([]);

    const buildVendorToDealsObj = () => {
        if (!specifyDealGridRowData) return {};
        return specifyDealGridRowData.reduce(
            (acc, curr) => {
                if (curr.contractName) {
                    if (!acc[curr.entityName!]) {
                        acc[curr.entityName!] = [];
                    }
                    acc[curr.entityName!].push(curr);
                }
                return acc;
            },
            {} as { [key: string]: ISpecifyDealAgGridRowData[] }
        );
    };

    const getDateCellClass = (params: ValueFormatterParams) => {
        if (!params.value) return '';
        let cellValue = params.value;
        if (typeof cellValue !== 'string')
            cellValue = cellValue.toLocaleString('en-CA', { year: 'numeric', month: 'numeric', day: 'numeric' });
        const isNewDeal = params.data.dealStatus === 'New';
        const isUpdatedDeal = params.data.dealStatus === 'Updated';
        const [currMon, currDay, currYear] = new Date().toLocaleDateString().split('/');
        const rerunDate = new Date(Number(currYear), Number(currMon) - 2, 1);
        const [cellYear, cellMon, cellDay] = cellValue.split('-');
        const cellDate = new Date(Number(cellYear), Number(cellMon) - 1, cellDay);

        /*
         * highlight start and/or end dates if
         * 1) the date belongs to a past accounting period
         * AND
         * 2) the date is part of a new deal OR the date of an existing deal was updated
         * */
        if (
            cellDate < rerunDate &&
            (isNewDeal ||
                (isUpdatedDeal &&
                    params.data[
                        `original${params.colDef.field!.charAt(0).toUpperCase() + params.colDef.field!.slice(1)}`
                    ] !== cellValue))
        ) {
            if (!requireRerun) setRequireRerun(true);
            return 'yellow-highlight-cell';
        } else {
            return '';
        }
    };

    const getClonedDeal = (deal: IDealsRowData) => {
        const clonedDeal = { ...deal };
        // @ts-ignore
        clonedDeal['service'] = clonedDeal.serviceName;
        // @ts-ignore
        clonedDeal['startDate'] = clonedDeal.startDate
            ? convertTimestampToYYYYMMDDInUTCTimezone(clonedDeal.startDate)
            : clonedDeal.startDate;
        // @ts-ignore
        clonedDeal['endDate'] = clonedDeal.endDate
            ? convertTimestampToYYYYMMDDInUTCTimezone(clonedDeal.endDate)
            : clonedDeal.endDate;

        return clonedDeal as IConfirmDealAgGridRowData;
    };

    const colDefs = [
        {
            headerName: 'Update',
            field: 'dealStatus',
            minWidth: 120
        },
        {
            headerName: 'Vendor',
            field: 'entityName',
            minWidth: 300
        },
        {
            headerName: 'Service',
            field: 'service',
            minWidth: 120
        },
        {
            headerName: 'Rate card',
            field: 'contractName',
            cellStyle: getDealStatusCellStyle,
            minWidth: 350
        },
        {
            headerName: 'Start date',
            field: 'startDate',
            comparator: dateComparator,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: dateFilterOptions,
                defaultOption: 'greaterThanOrEqual',
                comparator: dateFilterComparator,
                includeBlanksInLessThan: true,
                includeBlanksInGreaterThan: true
            },
            cellClass: (params: ValueFormatterParams) => {
                return [getDateCellClass(params), EXCEL_DATE_STYLE_ID];
            },
            cellStyle: getDealStatusCellStyle,
            minWidth: 150
        },
        {
            headerName: 'End date',
            field: 'endDate',
            comparator: dateComparator,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: dateFilterOptions,
                defaultOption: 'lessThanOrEqual',
                comparator: dateFilterComparator,
                includeBlanksInLessThan: true,
                includeBlanksInGreaterThan: true
            },
            cellClass: (params: ValueFormatterParams) => {
                return [getDateCellClass(params), EXCEL_DATE_STYLE_ID];
            },
            cellStyle: getDealStatusCellStyle,
            valueFormatter: emptyCellFormatter,
            minWidth: 150
        },
        {
            headerName: 'Comments',
            field: 'comment',
            filter: false,
            minWidth: 120
        }
    ];

    const sortModel: ColumnState[] = [
        { colId: 'entityName', sort: 'asc', sortIndex: 0 },
        { colId: 'service', sort: 'asc', sortIndex: 1 },
        { colId: 'startDate', sort: 'desc', sortIndex: 2 }
    ];

    useEffect(() => {
        const vendorToDealsObj = buildVendorToDealsObj();
        const newStatusToDealsObj = {
            new: [] as IConfirmDealAgGridRowData[],
            updated: [] as IConfirmDealAgGridRowData[],
            canceled: [] as IConfirmDealAgGridRowData[]
        };

        // process all the new added deals
        Object.keys(vendorToDealsObj).forEach((entityName) => {
            vendorToDealsObj[entityName].forEach((deal) => {
                newStatusToDealsObj['new'].push({ ...deal, dealStatus: 'New' });
            });
        });

        // process all existing deals that might be impacted by new added deals
        if (deals) {
            deals
                .filter((deal) => deal.status !== Status.Canceled)
                .forEach((dealObj) => {
                    const deal = getClonedDeal(dealObj);
                    if (vendorToDealsObj[deal.entityName!]) {
                        const newDealFromSpecify = vendorToDealsObj[deal.entityName!].find(
                            (specifiedDeal) => specifiedDeal.service === deal.service
                        );
                        if (newDealFromSpecify) {
                            const [newDealFromSpecifyStartDate, newDealFromSpecifyStartDateTs] = getDateAndTimestamp(
                                newDealFromSpecify.startDate
                            );
                            const [newDealFromSpecifyEndDate, newDealFromSpecifyEndDateTs] = getDateAndTimestamp(
                                newDealFromSpecify.endDate
                            );
                            const [existingDealStartDate, existingDealStartDateTs] = getDateAndTimestamp(
                                deal.startDate
                            );
                            const [existingDealEndDate, existingDealEndDateTs] = getDateAndTimestamp(deal.endDate);

                            const clonedExistingDeal: IClonedSpecifyDealAgGridRowData = clone(deal);
                            // originalStartDate and originalEndDate are used in getDateCellClass
                            clonedExistingDeal.originalStartDate = deal.startDate;
                            clonedExistingDeal.originalEndDate = deal.endDate;

                            // Bookend 1
                            if (
                                newDealFromSpecifyStartDateTs > existingDealStartDateTs &&
                                newDealFromSpecifyStartDateTs <= existingDealEndDateTs &&
                                newDealFromSpecifyEndDateTs >= existingDealEndDateTs
                            ) {
                                clonedExistingDeal.endDate = getYearMonthDayFormatFromISOString(
                                    deductDays(newDealFromSpecifyStartDate, 1)
                                );
                                newStatusToDealsObj['updated'].push({
                                    ...clonedExistingDeal,
                                    dealStatus: 'Updated',
                                    isStartDateUpdated: false
                                });
                            }
                            // Bookend 2
                            else if (
                                newDealFromSpecifyStartDateTs <= existingDealStartDateTs &&
                                newDealFromSpecifyEndDateTs < existingDealEndDateTs &&
                                newDealFromSpecifyEndDateTs > existingDealStartDateTs
                            ) {
                                clonedExistingDeal.startDate = getYearMonthDayFormatFromISOString(
                                    addDays(newDealFromSpecifyEndDate, 1)
                                );
                                newStatusToDealsObj['updated'].push({
                                    ...clonedExistingDeal,
                                    dealStatus: 'Updated',
                                    isStartDateUpdated: true
                                });
                            }
                            // Cancel existing
                            else if (
                                newDealFromSpecifyStartDateTs <= existingDealStartDateTs &&
                                newDealFromSpecifyEndDateTs >= existingDealEndDateTs
                            ) {
                                newStatusToDealsObj['canceled'].push({ ...clonedExistingDeal, dealStatus: 'Canceled' });
                            }
                            // Split existing
                            else if (
                                newDealFromSpecifyStartDateTs > existingDealStartDateTs &&
                                newDealFromSpecifyEndDateTs < existingDealEndDateTs
                            ) {
                                clonedExistingDeal.endDate = getYearMonthDayFormatFromISOString(
                                    deductDays(newDealFromSpecifyStartDate, 1)
                                );
                                newStatusToDealsObj['updated'].push({
                                    ...clonedExistingDeal,
                                    dealStatus: 'Updated',
                                    isStartDateUpdated: false
                                });
                                const newDeal2: IConfirmDealAgGridRowData = clone(deal);
                                newDeal2.startDate = getYearMonthDayFormatFromISOString(
                                    addDays(newDealFromSpecifyEndDate, 1)
                                );
                                newStatusToDealsObj['new'].push({ ...newDeal2, dealStatus: 'New' });
                            }
                        }
                    }
                });
        }

        const statusToUniqDealsObj = {
            new: cloneDeep(uniqWith(newStatusToDealsObj['new'], isEqual)),
            updated: cloneDeep(uniqWith(newStatusToDealsObj['updated'], isEqual)),
            canceled: cloneDeep(uniqWith(newStatusToDealsObj['canceled'], isEqual))
        };

        setStatusToDealsObj(statusToUniqDealsObj);
        setConfirmDealGridRowData(Object.values(statusToUniqDealsObj).flat());
    }, []);

    return (
        <div
            className="confirm-deal-div"
            style={{
                height: getWindowSize().height - (TOPBAR_HEIGHT + BREADCRUMB_HEIGHT + FOOTER_HEIGHT + HEIGHT_ADJUSTMENT)
            }}
        >
            <Text>Review the following deal updates. If everything looks correct, submit the updates.</Text>
            {requireRerun && (
                <Alert type="warning">Following deal updates may require reruns for past accounting periods.</Alert>
            )}
            <div className="deal-summary">
                <Text>
                    <span className="deal-count">{statusToDealsObj['new'].length}</span> New deals
                </Text>
                <Text>
                    <span className="deal-count">{statusToDealsObj['updated'].length}</span> Deals updated
                </Text>
                <Text>
                    <span className="deal-count">{statusToDealsObj['canceled'].length}</span> Deals canceled
                </Text>
            </div>
            <div className="confirm-deal-grid">
                <BaseAgGrid
                    // @ts-ignore
                    columnDefs={colDefs}
                    rowData={confirmDealGridRowData}
                    sortModel={sortModel}
                />
            </div>
        </div>
    );
};
